import React from "react";
import { Link } from "react-router-dom";

/**
 * Amazon-style vertical product card
 * Displays product image, title, price, discount badge, and stock status
 */
const ProductCard = ({ product }) => {
  const discount = (product.discount / 100) || 0;
  const discountedPrice = (product.price * (1 - discount)).toFixed(2);
  const firstImage = product.imageUrls?.[0];
  const showDiscount = discount > 0;
  const inStock = product.stockQuantity > 0;

  // Use slug for SEO-friendly URLs (new) or fallback to old format
  const productUrl = product.slug 
    ? `/p/${product.slug}` 
    : `/product/${encodeURIComponent(product.productName)}/${product.productId}`;

  return (
    <div className="bg-white rounded-sm border border-gray-200 hover:border-gray-300 transition-all duration-200 overflow-hidden group">
      <Link 
        to={productUrl} 
        className="block"
      >
        {/* Product Image Container */}
        <div className="relative bg-white p-4">
          {/* Discount Badge */}
          {showDiscount && (
            <div className="absolute top-2 left-2 bg-red-600 text-white text-xs font-bold px-2 py-1 rounded-sm z-10">
              -{(discount * 100).toFixed(0)}% OFF
            </div>
          )}
          
          {/* Product Image */}
          {firstImage ? (
            <img 
              src={firstImage} 
              alt={product.productName}
              className="w-full h-48 object-contain group-hover:scale-105 transition-transform duration-300" 
              onError={(e) => {
                e.target.src = '/api/placeholder/300/300';
              }}
            />
          ) : (
            <div className="w-full h-48 bg-gray-100 flex items-center justify-center">
              <span className="text-gray-400 text-sm">No Image</span>
            </div>
          )}
        </div>

        {/* Product Details */}
        <div className="p-3 space-y-1">
          {/* Product Title - Amazon style: 2 lines max */}
          <h3 className="text-sm text-gray-900 line-clamp-2 min-h-[2.5rem] leading-tight">
            {product.productName}
          </h3>

          {/* Rating - placeholder for now */}
          <div className="flex items-center space-x-1">
            <div className="flex text-orange-400 text-sm">
              {'★★★★☆'}
            </div>
            <span className="text-xs text-gray-500">(245)</span>
          </div>

          {/* Price Section */}
          <div className="space-y-0.5 pt-1">
            {showDiscount && (
              <div className="flex items-center space-x-2">
                <span className="text-xs bg-red-600 text-white px-1.5 py-0.5 rounded-sm font-semibold">
                  -{(discount * 100).toFixed(0)}%
                </span>
                <span className="text-xs text-gray-500 line-through">
                  KSH {Number(product.price).toLocaleString()}
                </span>
              </div>
            )}
            
            {/* Current Price - Amazon style: large and bold */}
            <div className="flex items-baseline space-x-1">
              <span className="text-xs text-gray-700">KSH</span>
              <span className="text-2xl font-normal text-gray-900">
                {Number(discountedPrice).toLocaleString()}
              </span>
            </div>
          </div>

          {/* Stock Status */}
          <div className={`text-xs font-medium pt-1 ${inStock ? 'text-green-700' : 'text-red-600'}`}>
            {inStock ? `In Stock` : "Out of Stock"}
          </div>

          {/* Delivery Info - Amazon style */}
          {inStock && (
            <div className="text-xs text-gray-600 pt-1">
              FREE delivery tomorrow
            </div>
          )}
        </div>
      </Link>
    </div>
  );
};

export default ProductCard;
